import React, { useCallback, useMemo, useState } from 'react';

import { Platform } from 'react-native';

import { useTranslation } from 'react-i18next';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { selectLayoutConfig } from 'store/config/selectors';
import {
    editProfile,
    editProfileFailure,
    editProfileRequestReset,
    getProfilesList,
    updateRatingsThresholds,
} from 'store/user/actions';
import {
    ParentalControlRating,
    RequestErrorData,
} from 'store/user/reducer.types';
import {
    selectActiveUserProfile,
    selectProfilesList,
} from 'store/user/selectors';

import { COLOR } from 'utils/colors';
import useEnterTheApp from 'utils/hooks/appBootstrap/useEnterTheApp';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';

import useUpdateCachedPins from 'screens/Settings/hooks/useUpdateCachedPins.ts';

import DialogView from 'components/Dialog';
import PinDialog, { PinDialogRef } from 'components/PinDialog';
import { PinDialogStatus } from 'components/PinDialog/hooks/getPinDialogText';
import usePinDialogStatus from 'components/PinDialog/hooks/usePinDialogStatus';
import { PinDialogProps } from 'components/PinDialog/types';

import styles from './styles';

const {
    NOTIFICATIONS: {
        NOTIFICATION_RELOAD_APP_AFTER_AGE_CHANGE_TITLE,
        NOTIFICATION_RELOAD_APP_AFTER_AGE_CHANGE_DESCRIPTION,
    },
    RELOAD_BUTTON,
} = LANGUAGE_KEYS;

export default function UpdateRatingsThresholdsDialog({
    visible,
    selectedRating,
    appState,
    onClose: onClosePress,
}: Pick<PinDialogProps, 'appState' | 'visible' | 'onClose'> & {
    selectedRating?: ParentalControlRating;
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { onEnterTheApp } = useEnterTheApp();
    const edges: Edge[] = useSafeAreaEdges();
    const { primary } = useSelector(selectLayoutConfig);
    const profiles = useSelector(selectProfilesList);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const pinDialogRef = React.useRef<PinDialogRef | null>(null);
    const [showReloadNotification, setShowReloadNotification] = useState(false);
    const [openAppError, setOpenAppError] = useState(false);

    const currentProfile = useMemo(
        () => profiles.find(({ _id }) => _id === selectedProfileId),
        [profiles, selectedProfileId],
    );

    const onUpdateCachedPins = useUpdateCachedPins();

    const onClose = useCallback(() => {
        pinDialogRef.current?.clear();
        onClosePress();
    }, [onClosePress]);

    const { pinDialogStatus, setPinDialogStatus, onValidatePin } =
        usePinDialogStatus(pinDialogRef, visible, onClose);

    const handlePinModalHide = useCallback(() => {
        setPinDialogStatus(PinDialogStatus.ENTER_PIN);
    }, []);

    const handleSuccessEditProfile = useCallback(() => {
        if (selectedRating && currentProfile && currentProfile.default) {
            dispatch(
                updateRatingsThresholds(selectedRating, {
                    autoRetry: true,
                    retryDuration: 10 * 60 * 1000,
                    retryCount: 10,
                }),
            );
        }

        onUpdateCachedPins();

        dispatch(getProfilesList());
        dispatch(editProfileRequestReset());

        pinDialogRef.current?.closeKeyboard();
        onClose();

        setTimeout(
            () => {
                setShowReloadNotification(true);
            },
            Platform.OS === 'ios' ? 500 : 0,
        );
    }, [selectedRating, currentProfile, onUpdateCachedPins, dispatch, onClose]);

    const handleSubmit = useCallback(
        (pin: string) => {
            if (selectedRating && currentProfile) {
                onValidatePin({
                    pin,
                    onPinValid: () => {
                        dispatch(
                            editProfile(
                                {
                                    id: currentProfile._id,
                                    ratingsThreshold: selectedRating,
                                },
                                {
                                    onSuccess: handleSuccessEditProfile,
                                },
                            ),
                        );
                    },
                });
            } else {
                dispatch(
                    editProfileFailure({
                        // @ts-ignore
                        response: {
                            data: { errorCode: 1111 } as RequestErrorData,
                        },
                    }),
                );
            }
        },
        [selectedRating, currentProfile, onValidatePin, dispatch],
    );
    const handleReload = useCallback(async () => {
        try {
            await onEnterTheApp(true);
            setOpenAppError(false);
        } catch (error) {
            setOpenAppError(true);
        }
    }, [onEnterTheApp]);

    return (
        <>
            <PinDialog
                ref={pinDialogRef}
                appState={appState}
                visible={visible && !showReloadNotification}
                status={pinDialogStatus}
                onClose={onClose}
                onSubmit={handleSubmit}
                onHide={handlePinModalHide}
                haveToShowCloseButton
            />

            {showReloadNotification && (
                <SafeAreaView edges={edges}>
                    <DialogView
                        visible
                        title={t(
                            openAppError
                                ? 'VITAL_REQUEST_FAILED_RETRY_TITLE'
                                : NOTIFICATION_RELOAD_APP_AFTER_AGE_CHANGE_TITLE,
                        )}
                        description={t(
                            openAppError
                                ? 'VITAL_REQUEST_FAILED_RETRY_DESCRIPTION'
                                : NOTIFICATION_RELOAD_APP_AFTER_AGE_CHANGE_DESCRIPTION,
                        )}
                        actionButtonTitle={t(RELOAD_BUTTON)}
                        actionButtonOnPress={handleReload}
                        actionButtonFillColor={primary || COLOR.primary}
                        actionButtonStyle={styles.button}
                        actionButtonTextStyle={styles.buttonText}
                        style={styles.modalContainer}
                    />
                </SafeAreaView>
            )}
        </>
    );
}
